import * as React from "react";
import {defineMessages, injectIntl} from "react-intl";
import {createDeleteButtonComponent} from "../../common/ui/DeleteButton";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {AOINotificationEvent} from "../model";
import InjectedIntlProps = ReactIntl.InjectedIntlProps;

const DeleteButtonComponent = createDeleteButtonComponent();

interface DeleteDataButtonProps {
    notificationId: string;
    allEvents: AOINotificationEvent[];
    selectedEvents: AOINotificationEvent[];
    disabled: boolean;
    onDeleteSuccess?: () => void;
    onDeleteError?: () => void;
    onDeleteWarnings?: () => void;
    onCloseWarnings?: () => void;
    detail?: boolean;
}

interface DeleteDataButtonState {
    data: AOINotificationEvent[];
}

enum DeleteAOIEventButtonMode {
    ALL,
    READ,
    UNREAD,
}

const MESSAGES = defineMessages({
    removeAOIEventHeader: {
        id: "studio.aoi-event.delete-aoi-event-button.delete-aoi-event-header",
        defaultMessage: "Remove {Amount} AOI events",
    },
    removeSingleAOIEventHeader: {
        id: "studio.aoi-event.delete-aoi-event-button.delete-single-aoi-event-header",
        defaultMessage: "Remove AOI event",
    },
    removeAOIEventBody: {
        id: "studio.aoi-event.delete-aoi-event-button.remove-aoi-event-body",
        defaultMessage: "Are you sure you want to continue?",
    },
    whileRemoveAOIEvent: {
        id: "studio.aoi-event.delete-aoi-event-button.while-remove-aoi-event",
        defaultMessage: "Removing AOI event...",
    },
    deleteButton: {
        id: "studio.aoi-event.delete-aoi-event-button.delete-button",
        defaultMessage: "Remove AOI event",
    },
    removeStylesSuccess: {
        id: "studio.aoi-event.delete-aoi-event-button.remove-success",
        defaultMessage: "AOI event successfully removed",
    },
    tooltipMessageNoSelection: {
        id: "studio.aoi-event.delete-aoi-event-button.no-selection-tooltip",
        defaultMessage: "Select AOI event from the list below",
    },
    tooltipMessageOneItemSelection: {
        id: "studio.aoi-event.delete-aoi-event-button.one-item-selection-tooltip",
        defaultMessage: "You don't have permission to delete the AOI event",
    },
    tooltipMessageItemsSelection: {
        id: "studio.aoi-event.delete-aoi-event-button.items-selection-tooltip",
        defaultMessage: "You don't have permission to delete the AOI event",
    },
    buttonTitle: {
        id: "studio.aoi-event.delete-aoi-event-button.delete-aoi-event",
        defaultMessage: "Remove...",
    },
    buttonSelectedTitle: {
        id: "studio.aoi-event.delete-aoi-event-button.delete-selected-aoi-event",
        defaultMessage: "Remove",
    },
    buttonTitleDetail: {
        id: "studio.aoi-event.delete-aoi-event-button.remove",
        defaultMessage: "Remove",
    },
    allModeTitle: {
        id: "studio.aoi-event.delete-aoi-event-button.all-mode-title",
        defaultMessage: "Remove all",
    },
    readModeTitle: {
        id: "studio.aoi-event.delete-aoi-event-button.read-mode-title",
        defaultMessage: "Remove read",
    },
    unreadModeTitle: {
        id: "studio.aoi-event.delete-aoi-event-button.unread-mode-title",
        defaultMessage: "Remove unread",
    },
    emptyModeTitle: {
        id: "studio.aoi-event.delete-aoi-event-button.empty-mode-title",
        defaultMessage: "Remove empty",
    },
    deleteConfirmationBody: {
        id: "studio.data.delete-data-button.confirmation-body",
        defaultMessage: "Are you sure you want to continue?",
    }
});

class DeleteDataButtonComponent extends React.Component<DeleteDataButtonProps & InjectedIntlProps & WithApiProperties, DeleteDataButtonState> {
    constructor(props) {
        super(props);

        this.state = {
            data: [],
        };
    }

    render() {
        const {
            onDeleteSuccess,
            onDeleteError,
            onDeleteWarnings,
            onCloseWarnings,
            intl,
            disabled,
            detail,
            selectedEvents
        } = this.props;
        const {data} = this.state;
        const headerText = intl.formatMessage(MESSAGES.removeAOIEventHeader, {Amount: data.length});
        let buttonTitle = intl.formatMessage(MESSAGES.buttonTitle);
        if (selectedEvents.length > 0) {
            buttonTitle = intl.formatMessage(MESSAGES.buttonSelectedTitle);
        }

        return <DeleteButtonComponent
            intl={this.props.intl}
            disabled={disabled}
            onBeforeDelete={this.onBeforeDelete}
            onDelete={this.onDelete}
            onDeleteSuccess={onDeleteSuccess}
            onDeleteError={onDeleteError}
            onDeleteWarnings={onDeleteWarnings}
            onCloseWarnings={onCloseWarnings}
            dialogTitle={headerText}
            buttonTitle={buttonTitle}
            detail={detail}
            tooltipDisabled={""}
            successMessage={intl.formatMessage(MESSAGES.removeStylesSuccess)}
            executingMessage={intl.formatMessage(MESSAGES.whileRemoveAOIEvent)}
            confirmButtonText={intl.formatMessage(MESSAGES.deleteButton)}
            modes={selectedEvents.length === 0 ? [
                {
                    key: DeleteAOIEventButtonMode.ALL,
                    title: intl.formatMessage(MESSAGES.allModeTitle),
                },
                {
                    key: DeleteAOIEventButtonMode.READ,
                    title: intl.formatMessage(MESSAGES.readModeTitle),
                },
                {
                    key: DeleteAOIEventButtonMode.UNREAD,
                    title: intl.formatMessage(MESSAGES.unreadModeTitle),
                },
            ] : null}
            renderConfirmation={() => {
                return (
                    <div>
                        {intl.formatMessage(MESSAGES.deleteConfirmationBody)}
                    </div>
                );
            }}
        />;
    }

    private onBeforeDelete = (modeKey: DeleteAOIEventButtonMode) => {
        this.setState({
            data: this.getDataToDelete(modeKey),
        });

        return Promise.resolve();
    };

    private getDataToDelete = (modeKey) => {
        const {allEvents, selectedEvents} = this.props;

        if (selectedEvents.length > 0) {
            return selectedEvents;
        }

        switch (modeKey) {
            case DeleteAOIEventButtonMode.ALL:
                return allEvents;
            case DeleteAOIEventButtonMode.READ:
                return allEvents.filter(e => e.hasBeenRead);
            case DeleteAOIEventButtonMode.UNREAD:
                return allEvents.filter(e => !e.hasBeenRead);
        }
    };

    private onDelete = () => {
        const {api, notificationId} = this.props;
        const {data} = this.state;

        return api.deleteNotificationEvent(notificationId, data.map(d => d.id))
            .then(r => null);
    }
}

export const DeleteAOIEventButton = WithApi(injectIntl(DeleteDataButtonComponent));